import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  Typography,
  Button,
  TableCell,
  TableRow,
  Table,
  Pagination,
  CheckBox,
  Select,
  useSelect,
  Input,
  Dialog,
  CircleWarningIcon,
  CircleSuccessIcon,
  CircleErrorIcon,
  Spinner,
  Card,
  Tag,
  DatePicker,
  useDatePicker,
  useInput,
  Toast,
  InputSuffix
} from '@chilecompra/react-kit/components'
import { useParams } from 'react-router-dom'
import { GridContainer, GridItem, Root, Title, Subtitle, Content, ContainerSwitch } from './AgreementAdjustment.styles'
import { Divider } from '@mui/material'
import {
  ADJUSTMENT_STATUS,
  localStorageOrgCode,
  localStorageProviderId,
  TAGS_STATUS_COLOR
} from '../../config/settings/constants'
import {
  onGetAbilityConfigThunk,
  onGetAgreementThunk,
  onGetProductsThunk,
  onGetRequestsThunk,
  onGetProductsTypeThunk,
  onGetRutThunk,
  onPostCreateRequestThunk,
  onDownloadRequestThunk,
  onGetAgreementConfigThunk,
  onGetActiveOfferThunk
} from './AgreementAdjustment.actions'
import { NumericFormat } from 'react-number-format'
import { formatDate } from '@chilecompra/react-kit/utils'

/**
 * The AgreementAdjustment's container.
 */

const AgreementAdjustment = () => {
  const dispatch = useDispatch()
  const [agreement, setAgreement] = useState({
    idConvenioMarco: '',
    nombreConvenioMarco: ''
  })
  const [productsSelected, setProductsSelected] = useState([])
  const [products, setProducts] = useState({
    productList: [],
    page: 0,
    totalPages: 0
  })
  const [productsLoading, setProductsLoading] = useState(false)
  const [productsFilters, setProductsFilters] = useState({
    idTipoProducto: 0,
    nombreProducto: '',
    page: 1,
    idConvenioMarco: 0,
    idProveedor: 0,
    sort: {
      column: 'nombreProducto',
      sort: 'asc'
    }
  })
  const { id } = useParams()
  const [statusUpdate, setStatusUpdate] = useState('')
  const [openSelected, setOpenSelected] = useState(false)
  const [loading, setLoading] = useState(false)
  const [haveAbility, setHaveAbility] = useState(false)
  const providerId = localStorage.getItem(localStorageProviderId)
  const orgCode = localStorage.getItem(localStorageOrgCode)
  const [agreementLoading, setAgreementLoading] = useState(true)
  const [adjustments, setAdjustments] = useState({
    requestList: [],
    page: 0,
    totalPages: 0
  })
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(true)
  const [adjustmentsFilters, setAdjustmentsFilters] = useState({
    estadoReajuste: '',
    idProducto: 0,
    nombreProducto: '',
    fechaSolicitud: '',
    page: 1,
    sort: {
      column: 'nombreProducto',
      sort: 'asc'
    }
  })
  const [productsType, setProductsType] = useState([])
  const isChecked = function (idProducto) {
    return productsSelected.find(function (element) {
      return element.idProducto === idProducto
    })
  }
  const [rutProvider, setRutProvider] = useState('')
  const [overwriteValue, setOverwriteValue] = useState(0)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [config, setConfig] = useState({})

  const handleChange = e => {
    const { value, checked } = e.target
    if (checked) {
      setProductsSelected(prev => [
        ...prev,
        products.productList.find(product => Number(product.idProducto) === Number(value))
      ])
    } else {
      setProductsSelected(prev => prev.filter(x => Number(x.idProducto) !== Number(value)))
    }
  }

  const { error: productTypeError, value: productType } = useSelect({
    initialValue: 0,
    overwriteCallback: () => overwriteValue
  })

  const {
    error: searchError,
    value: search,
    onChange: handleSearch
  } = useSelect({
    overwriteCallback: () => productsFilters.search || ''
  })

  const requestAdjustment = useCallback(() => {
    if (rutProvider) {
      dispatch(
        onPostCreateRequestThunk({
          successCallback: w => {
            setLoading(false)
            setOpenSelected(false)
            const pendingCount = w.filter(e => {
              return e.solicitudStatus.toLowerCase() === 'pendiente'
            })
            if (pendingCount.length) {
              setStatusUpdate('success_pending')
            } else {
              setStatusUpdate('success')
            }
            setProductsSelected([])
            getProducts()
            getAdjustmentRequest()
          },
          errorCallback: () => {
            setLoading(false)
            setOpenSelected(false)
            setStatusUpdate('error')
          },
          loading: () => {
            setLoading(true)
          },
          payload: {
            idProveedor: providerId,
            rutProveedor: rutProvider,
            idConvenioMarco: id,
            products: productsSelected.map(_product => ({
              idProducto: _product.idProducto,
              nombreProducto: _product.nombre,
              precioRegular: _product.precio
            }))
          }
        })
      )
    }
  }, [rutProvider, productsSelected])

  const getAdjustmentcolor = function (status) {
    return (
      TAGS_STATUS_COLOR.find(e => {
        return e.value.toLowerCase() === status.toLowerCase()
      })?.color ?? 'default'
    )
  }

  const {
    error: adjustmentStatusError,
    value: adjustmentStatus,
    onChange: handleChangeAdjustmentStatus,
    onReset: resetAdjustmentStatus
  } = useSelect({
    overwriteCallback: () => adjustmentsFilters.estadoReajuste || ''
  })

  const {
    error: productIdError,
    value: productId,
    onChange: handleChangeProductId,
    onReset: resetProductId
  } = useInput({
    overwriteCallback: () => adjustmentsFilters.idProducto || ''
  })

  const {
    error: productNameError,
    value: productName,
    onChange: handleChangeProductName,
    onReset: resetProductName
  } = useInput({
    overwriteCallback: () => adjustmentsFilters.nombreProducto || ''
  })

  const {
    error: requestDateError,
    value: requestDate,
    onChange: handleChangeRequestDate,
    onReset: resetRequestDate
  } = useDatePicker({
    overwriteCallback: () => (adjustmentsFilters.fechaSolicitud ? new Date(adjustmentsFilters.fechaSolicitud) : null)
  })

  const verifyActiveOffter = useCallback(
    data => {
      data.forEach(_product => {
        const _payload = {
          sku: _product.idProducto,
          orgCode,
          agreement: id
        }
        dispatch(
          onGetActiveOfferThunk({
            callback: w => {
              setProducts(prev => ({
                ...prev,
                productList: prev.productList.map(old =>
                  old.idProducto === _product.idProducto ? { ...old, isValid: w !== '1', loading: false } : old
                )
              }))
            },
            payload: {
              data: _payload
            }
          })
        )
      })
    },
    [config, products, orgCode, productsLoading]
  )

  const getProducts = function (payload = productsFilters) {
    dispatch(
      onGetProductsThunk({
        callback: w => {
          if (w) {
            const productList = w.productList.map(e => {
              return {
                ...e,
                isValid: false,
                loading: false
              }
            })
            setProducts({
              productList,
              page: w.page,
              totalPages: w.totalPages
            })
            verifyActiveOffter(productList)
          }
          setProductsLoading(false)
        },
        loading: () => {
          setProductsLoading(true)
        },
        payload
      })
    )
  }

  const handleChangeOrderProducts = useCallback(
    (column, sort = 'asc') => {
      setProductsFilters(prevState => {
        const object = Object.assign({}, prevState)
        object.sort.column = column
        object.sort.sort = sort
        return { object }
      })
      assigFiltersToProducts()
    },
    [productsFilters]
  )

  const handleChangeProductTypes = useCallback(
    e => {
      setOverwriteValue(e.target.value)
      filterGetProducts({
        idTipoProducto: e.target.value
      })
    },
    [productType, overwriteValue]
  )

  const assigFiltersToProducts = useCallback(() => {
    const params = {
      idTipoProducto: productType ?? 0,
      nombreProducto: search ?? '',
      page: 1,
      idConvenioMarco: Number(id),
      idProveedor: Number(providerId),
      sort: productsFilters.sort
    }
    setProductsFilters(params)
    getProducts(params)
  }, [productsFilters, productType, search, config])

  const filterGetProducts = function (params) {
    const _params = {
      idTipoProducto: params.idTipoProducto ?? productsFilters.idTipoProducto ?? 0,
      nombreProducto: productsFilters.nombreProducto,
      page: params?.page ?? productsFilters.page,
      idConvenioMarco: Number(id),
      idProveedor: Number(providerId),
      sort: productsFilters.sort
    }
    getProducts(_params)
  }

  const getAdjustmentRequest = function (payload = adjustmentsFilters) {
    const data = { ...payload }
    data.fechaSolicitud = payload.fechaSolicitud ? formatDate(payload.fechaSolicitud, 'yyyy-MM-dd') : ''
    dispatch(
      onGetRequestsThunk({
        callback: w => {
          if (w) {
            setAdjustments(w)
          }
          setAdjustmentsLoading(false)
        },
        loading: () => {
          setAdjustmentsLoading(true)
        },
        payload: data
      })
    )
  }

  const downloadAdjustmentRequest = useCallback(() => {
    const data = { ...adjustmentsFilters }
    data.fechaSolicitud = adjustmentsFilters.fechaSolicitud
      ? formatDate(adjustmentsFilters.fechaSolicitud, 'yyyy-MM-dd')
      : ''
    dispatch(
      onDownloadRequestThunk({
        callback: w => {
          const url = window.URL.createObjectURL(new Blob([w]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'Historico.csv')
          document.body.appendChild(link)
          link.click()
          link.remove()
          setDownloadLoading(false)
        },
        loading: () => {
          setDownloadLoading(true)
        },
        payload: data
      })
    )
  }, [adjustmentsFilters])

  const handleChangeOrderRequest = useCallback(
    (column, sort = 'asc') => {
      setAdjustmentsFilters(prevState => {
        const object = Object.assign({}, prevState)
        object.sort.column = column
        object.sort.sort = sort
        return { object }
      })
      assigFiltersToRequest()
    },
    [adjustmentsFilters]
  )

  const assigFiltersToRequest = useCallback(() => {
    const params = {
      estadoReajuste: adjustmentStatus ?? '',
      idProducto: Number(productId) ?? 0,
      nombreProducto: productName ?? '',
      fechaSolicitud: requestDate ?? '',
      page: 1,
      idConvenioMarco: Number(id),
      idProveedor: Number(providerId),
      sort: adjustmentsFilters.sort
    }
    setAdjustmentsFilters(params)
    getAdjustmentRequest(params)
  }, [adjustmentsFilters, adjustmentStatus, productId, productName, requestDate])

  const filterGetAdjustment = function (params) {
    const _params = {
      estadoReajuste: adjustmentsFilters.estadoReajuste,
      idProducto: adjustmentsFilters.idProducto,
      nombreProducto: adjustmentsFilters.nombreProducto,
      fechaSolicitud: adjustmentsFilters.fechaSolicitud,
      page: params?.page ?? adjustmentsFilters.page,
      idConvenioMarco: Number(id),
      idProveedor: Number(providerId),
      sort: adjustmentsFilters.sort
    }
    setAdjustmentsFilters(_params)
    getAdjustmentRequest(_params)
  }
  const getRutProvider = function () {
    dispatch(
      onGetRutThunk({
        callback: w => {
          if (w) {
            setRutProvider(w.rut ?? '')
          }
        },
        providerId
      })
    )
  }

  const getHablity = function () {
    dispatch(
      onGetAbilityConfigThunk({
        callback: w => {
          if (w) {
            setHaveAbility(w)
          }
        },
        payload: {
          providerId,
          convenioId: id
        }
      })
    )
  }

  const getAgreement = function () {
    dispatch(
      onGetAgreementThunk({
        callback: w => {
          setAgreementLoading(false)
          if (w) {
            setAgreement(w)
          }
        },
        loading: () => {
          setAgreementLoading(true)
        },
        id
      })
    )
  }

  const getProductsType = function () {
    dispatch(
      onGetProductsTypeThunk({
        callback: w => {
          if (w) {
            setProductsType(
              w.map(val => ({
                value: val.idTipoProducto,
                name: val.tpnombre
              }))
            )
            setProductsType(prev => [
              {
                value: 0,
                name: 'TODOS'
              },
              ...prev
            ])
          }
        },
        id
      })
    )
  }

  const generateDate = function (time) {
    const _time = new Date(time)
    return `${_time.getDate()}/${_time.getMonth() + 1}/${_time.getFullYear()}`
  }

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  const resetFilters = function () {
    resetAdjustmentStatus()
    resetProductId()
    resetProductName()
    resetRequestDate()
  }

  useEffect(() => {
    getRutProvider()
  }, [rutProvider])

  useEffect(() => {
    dispatch(
      onGetAgreementConfigThunk({
        callback: w => {
          setConfig(w)
        },
        id
      })
    )
    getHablity()
    getAgreement()
    getProductsType()
    assigFiltersToRequest()
  }, [])

  useEffect(() => {
    assigFiltersToProducts()
  }, [config])

  return (
    <Root>
      {agreementLoading ? (
        <Spinner />
      ) : !agreement.idConvenioMarco ? (
        <Toast severity="error">Ha ocurrido un error, vuelva a intentar.</Toast>
      ) : (
        <Root>
          <GridContainer>
            <GridItem xs={12}>
              <Title>Convenio Marco De {agreement?.nombreConvenioMarco}</Title>
              <Typography variant="h4">ID Licitación: {agreement?.nroLicitacionPublica}</Typography>
            </GridItem>
          </GridContainer>
          {haveAbility ? (
            <Content>
              <Subtitle>Listado de productos sin reajuste vigente</Subtitle>
              <GridContainer className="filterProductsContainer">
                <GridItem padding="0 5px" sm={4} md={4} lg={3}>
                  <Select
                    label="tipo de producto"
                    options={productsType}
                    value={productType}
                    error={productTypeError}
                    onChange={e => handleChangeProductTypes(e)}
                  />
                </GridItem>
                <GridItem padding="0 5px" sm={4} md={4} lg={3}>
                  <Input
                    label="Buscar por nombre de producto"
                    value={search}
                    error={searchError}
                    onChange={handleSearch}
                    placeholder="Nombre producto"
                    suffix={
                      <InputSuffix width="60px" onClick={() => assigFiltersToProducts()}>
                        Buscar
                      </InputSuffix>
                    }
                  />
                </GridItem>
              </GridContainer>
              <GridContainer className="containerTable">
                <Table margin="15px 0" loading={productsLoading}>
                  <TableRow enableHead>
                    <TableCell />
                    <TableCell
                      className={`orderableCell order${capitalizeFirstLetter(productsFilters.sort.sort)} ${
                        productsFilters.sort.column === 'idProducto' ? 'active' : ''
                      }`}
                      onClick={() =>
                        handleChangeOrderProducts(
                          'idProducto',
                          productsFilters.sort.column !== 'idProducto'
                            ? 'asc'
                            : productsFilters.sort.sort === 'asc'
                            ? 'desc'
                            : 'asc'
                        )
                      }
                    >
                      ID Producto
                    </TableCell>
                    <TableCell> Tipo Producto </TableCell>
                    <TableCell
                      className={`orderableCell order${capitalizeFirstLetter(productsFilters.sort.sort)} ${
                        productsFilters.sort.column === 'nombreProducto' ? 'active' : ''
                      }`}
                      onClick={() =>
                        handleChangeOrderProducts(
                          'nombreProducto',
                          productsFilters.sort.column !== 'nombreProducto'
                            ? 'asc'
                            : productsFilters.sort.sort === 'asc'
                            ? 'desc'
                            : 'asc'
                        )
                      }
                    >
                      Producto
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'right'
                      }}
                      className={`orderableCell order${capitalizeFirstLetter(productsFilters.sort.sort)} ${
                        productsFilters.sort.column === 'precio' ? 'active' : ''
                      }`}
                      onClick={() =>
                        handleChangeOrderProducts(
                          'precio',
                          productsFilters.sort.column !== 'precio'
                            ? 'asc'
                            : productsFilters.sort.sort === 'asc'
                            ? 'desc'
                            : 'asc'
                        )
                      }
                    >
                      Precio Regular
                    </TableCell>
                  </TableRow>
                  {products?.productList?.length > 0 ? (
                    products.productList.map((product, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {product.isValid ? (
                            <CheckBox
                              disabled={!haveAbility}
                              onChange={handleChange}
                              checked={isChecked(product.idProducto)}
                              value={product.idProducto}
                            />
                          ) : (
                            ''
                          )}
                        </TableCell>
                        <TableCell> {product.idProducto} </TableCell>
                        <TableCell> {product.tpnombre} </TableCell>
                        <TableCell> {product.nombre} </TableCell>
                        <TableCell
                          style={{
                            textAlign: 'right'
                          }}
                        >
                          <NumericFormat
                            displayType="text"
                            value={product.precio}
                            decimalSeparator=","
                            prefix="$"
                            thousandSeparator="."
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} textAlign="center">
                        <div
                          style={{
                            width: '100%',
                            textAlign: 'center',
                            opacity: '0.8'
                          }}
                        >
                          No hay resultados
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Table>
              </GridContainer>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'end',
                  marginBottom: '15px',
                  marginTop: '5px'
                }}
              >
                <Pagination
                  count={products.totalPages}
                  disableFirstButton
                  disableLastButton
                  onChange={(event, page) => {
                    filterGetProducts({ page: page })
                  }}
                  page={products.page}
                  variant="contained"
                />
              </div>
              <Divider orientation="horizontal" />
              <div
                className="containerButtons"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'end',
                  marginTop: '15px'
                }}
              >
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    setOpenSelected(haveAbility)
                  }}
                  disabled={productsSelected.length === 0 || !haveAbility}
                >
                  Solicitar Reajuste
                </Button>
              </div>
            </Content>
          ) : (
            ''
          )}
          <Content>
            <Subtitle> Listado de reajustes históricos</Subtitle>
            <Card enableBorder>
              <GridContainer className="containerFilterInputs">
                <GridItem padding="0 5px">
                  <Select
                    label="estado del reajuste"
                    options={ADJUSTMENT_STATUS}
                    value={adjustmentStatus}
                    error={adjustmentStatusError}
                    onChange={handleChangeAdjustmentStatus}
                  />
                </GridItem>
                <GridItem padding="0 5px">
                  <Input
                    label="id producto"
                    value={productId}
                    error={productIdError}
                    onChange={handleChangeProductId}
                    placeholder="000000000"
                  />
                </GridItem>
                <GridItem padding="0 5px">
                  <Input
                    label="nombre producto"
                    value={productName}
                    error={productNameError}
                    onChange={handleChangeProductName}
                    placeholder="Nombre producto"
                  />
                </GridItem>
                <GridItem padding="0 5px">
                  <DatePicker
                    label="Fecha solicitud"
                    placeholder="12/07/2021"
                    value={requestDate}
                    error={requestDateError}
                    onChange={handleChangeRequestDate}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer className="containerSubmitButtonFilter">
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'end'
                  }}
                >
                  <div
                    style={{
                      marginRight: '10px'
                    }}
                  >
                    <Button
                      onClick={() => resetFilters(true)}
                      variant="outlined"
                      style={{
                        margin: '5px'
                      }}
                    >
                      Limpiar
                    </Button>
                  </div>
                  <Button color="primary" margin="5px" variant="contained" onClick={() => assigFiltersToRequest()}>
                    Buscar
                  </Button>
                </div>
              </GridContainer>
            </Card>
            <GridContainer className="containerTable">
              <Table margin="15px 0" loading={adjustmentsLoading}>
                <TableRow enableHead>
                  <TableCell
                    className={`orderableCell order${capitalizeFirstLetter(adjustmentsFilters.sort.sort)} ${
                      adjustmentsFilters.sort.column === 'solicitudStatus' ? 'active' : ''
                    }`}
                    onClick={() =>
                      handleChangeOrderRequest(
                        'solicitudStatus',
                        adjustmentsFilters.sort.column !== 'solicitudStatus'
                          ? 'asc'
                          : adjustmentsFilters.sort.sort === 'asc'
                          ? 'desc'
                          : 'asc'
                      )
                    }
                  >
                    Estado Reajuste
                  </TableCell>
                  <TableCell
                    className={`orderableCell order${capitalizeFirstLetter(adjustmentsFilters.sort.sort)} ${
                      adjustmentsFilters.sort.column === 'rutProveedor' ? 'active' : ''
                    }`}
                    onClick={() =>
                      handleChangeOrderRequest(
                        'rutProveedor',
                        adjustmentsFilters.sort.column !== 'rutProveedor'
                          ? 'asc'
                          : adjustmentsFilters.sort.sort === 'asc'
                          ? 'desc'
                          : 'asc'
                      )
                    }
                  >
                    RUT Proveedor
                  </TableCell>
                  <TableCell
                    className={`orderableCell order${capitalizeFirstLetter(adjustmentsFilters.sort.sort)} ${
                      adjustmentsFilters.sort.column === 'idProducto' ? 'active' : ''
                    }`}
                    onClick={() =>
                      handleChangeOrderRequest(
                        'idProducto',
                        adjustmentsFilters.sort.column !== 'idProducto'
                          ? 'asc'
                          : adjustmentsFilters.sort.sort === 'asc'
                          ? 'desc'
                          : 'asc'
                      )
                    }
                  >
                    ID Producto
                  </TableCell>
                  <TableCell
                    className={`orderableCell order${capitalizeFirstLetter(adjustmentsFilters.sort.sort)} ${
                      adjustmentsFilters.sort.column === 'nombreProducto' ? 'active' : ''
                    }`}
                    onClick={() =>
                      handleChangeOrderRequest(
                        'nombreProducto',
                        adjustmentsFilters.sort.column !== 'nombreProducto'
                          ? 'asc'
                          : adjustmentsFilters.sort.sort === 'asc'
                          ? 'desc'
                          : 'asc'
                      )
                    }
                  >
                    Producto
                  </TableCell>
                  <TableCell
                    style={{
                      textAlign: 'right'
                    }}
                    className={`orderableCell order${capitalizeFirstLetter(adjustmentsFilters.sort.sort)} ${
                      adjustmentsFilters.sort.column === 'precioRegular' ? 'active' : ''
                    }`}
                    onClick={() =>
                      handleChangeOrderRequest(
                        'precioRegular',
                        adjustmentsFilters.sort.column !== 'precioRegular'
                          ? 'asc'
                          : adjustmentsFilters.sort.sort === 'asc'
                          ? 'desc'
                          : 'asc'
                      )
                    }
                  >
                    Precio Regular
                  </TableCell>
                  <TableCell
                    style={{
                      textAlign: 'right'
                    }}
                    className={`orderableCell order${capitalizeFirstLetter(adjustmentsFilters.sort.sort)} ${
                      adjustmentsFilters.sort.column === 'precioReajustado' ? 'active' : ''
                    }`}
                    onClick={() =>
                      handleChangeOrderRequest(
                        'precioReajustado',
                        adjustmentsFilters.sort.column !== 'precioReajustado'
                          ? 'asc'
                          : adjustmentsFilters.sort.sort === 'asc'
                          ? 'desc'
                          : 'asc'
                      )
                    }
                  >
                    Precio Reajustado
                  </TableCell>
                  <TableCell> Porcentaje reajuste </TableCell>
                  <TableCell> Fecha solicitud </TableCell>
                  <TableCell> Fecha respuesta </TableCell>
                  <TableCell> Vigencia </TableCell>
                  <TableCell> Observaciones </TableCell>
                </TableRow>
                {adjustments?.requestList?.length > 0 ? (
                  adjustments.requestList.map((adjustment, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Tag text={adjustment.reajusteStatus} color={getAdjustmentcolor(adjustment.reajusteStatus)} />
                      </TableCell>
                      <TableCell> {adjustment.rutProveedor} </TableCell>
                      <TableCell> {adjustment.idProducto} </TableCell>
                      <TableCell> {adjustment.nombreProducto} </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right'
                        }}
                      >
                        <NumericFormat
                          displayType="text"
                          value={adjustment.precioRegular}
                          decimalSeparator=","
                          prefix="$"
                          thousandSeparator="."
                        />
                      </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right'
                        }}
                      >
                        <NumericFormat
                          displayType="text"
                          value={adjustment.precioReajustado}
                          decimalSeparator=","
                          prefix="$"
                          thousandSeparator="."
                        />
                      </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right'
                        }}
                      >
                        <NumericFormat
                          displayType="text"
                          value={adjustment.porcentajeReajuste}
                          suffix="%"
                          decimalSeparator=","
                        />
                      </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right'
                        }}
                      >
                        {generateDate(adjustment.fechaSolicitud)}
                      </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right'
                        }}
                      >
                        {generateDate(adjustment.fechaRespuesta)}
                      </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right'
                        }}
                      >
                        {generateDate(adjustment.vigencia)}
                      </TableCell>
                      <TableCell> {adjustment.observaciones} </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} textAlign="center">
                      <div
                        style={{
                          width: '100%',
                          textAlign: 'center',
                          opacity: '0.8'
                        }}
                      >
                        No hay resultados
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Table>
            </GridContainer>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                marginBottom: '15px',
                marginTop: '5px'
              }}
            >
              <Pagination
                count={adjustments.totalPages}
                disableFirstButton
                disableLastButton
                onChange={(event, page) => {
                  filterGetAdjustment({ page })
                }}
                page={adjustments.page}
                variant="contained"
              />
              <ContainerSwitch>
                <Button
                  color="primary"
                  margin="5px"
                  variant="text"
                  onClick={() => downloadAdjustmentRequest()}
                  loading={downloadLoading}
                >
                  Descargar datos
                </Button>
              </ContainerSwitch>
            </div>
          </Content>
          <Dialog
            disableCloseIcon={loading}
            open={openSelected}
            disableBackdropClick
            onClose={() => {
              if (!loading) {
                setOpenSelected(false)
              }
            }}
          >
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '20px'
              }}
            >
              <CircleWarningIcon
                style={{
                  fontSize: '64px'
                }}
              />
              <div
                style={{
                  margin: '16px 0 0',
                  textAlign: 'center',
                  width: '100%'
                }}
              >
                <Typography fontWeight="bold" variant="h4">
                  Estás a punto de solicitar un reajuste de precio
                </Typography>
                <Typography margin="16px 0 0" variant="body2">
                  Antes de finalizar, revisa el detalle de los precios que serán reajustados.
                </Typography>
              </div>
              <GridContainer className="containerTableModal">
                <Table margin="15px 0">
                  <TableRow enableHead>
                    <TableCell> ID Producto </TableCell>
                    <TableCell> RUT Proveedor </TableCell>
                    <TableCell> Precio Regular </TableCell>
                  </TableRow>
                  {productsSelected.map((product, index) => (
                    <TableRow key={index}>
                      <TableCell> {product.idProducto} </TableCell>
                      <TableCell> {rutProvider} </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right'
                        }}
                      >
                        <NumericFormat
                          displayType="text"
                          value={product.precio}
                          decimalSeparator=","
                          prefix="$"
                          thousandSeparator="."
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              </GridContainer>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  marginTop: '20px'
                }}
              >
                {loading ? (
                  <Spinner />
                ) : (
                  <Button onClick={() => requestAdjustment()} color="success">
                    Solicitar reajuste
                  </Button>
                )}
              </div>
            </div>
          </Dialog>
          <Dialog
            open={statusUpdate}
            disableBackdropClick
            onClose={() => {
              if (!loading) {
                setStatusUpdate(false)
              }
            }}
          >
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '20px'
              }}
            >
              {statusUpdate === 'success' || statusUpdate === 'success_pending' ? (
                <CircleSuccessIcon
                  style={{
                    fontSize: '64px'
                  }}
                />
              ) : (
                <CircleErrorIcon
                  style={{
                    fontSize: '64px'
                  }}
                />
              )}

              <div
                style={{
                  margin: '16px 0 0',
                  textAlign: 'center',
                  width: '100%'
                }}
              >
                <Typography fontWeight="bold" variant="h4">
                  {statusUpdate === 'success'
                    ? 'Reajuste de precio solicitado satisfactoriamente'
                    : statusUpdate === 'success_pending'
                    ? 'Reajuste de precio solicitado'
                    : 'El reajuste de precio no pudo ser solicitado'}
                </Typography>
                <Typography margin="16px 0 0" variant="body2">
                  {statusUpdate === 'error'
                    ? 'Lo sentimos, no pudimos envíar la solicitud, por favor inténtalo en unos minutos más.'
                    : statusUpdate === 'success_pending'
                    ? 'Algunas solicitudes de reajuste de precios quedaron en estado "Pendiente" a la espera de la revisión del encargo del Convenio Marco.'
                    : ''}
                </Typography>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  marginTop: '20px'
                }}
              >
                <Button onClick={() => setStatusUpdate(false)}> Cerrar </Button>
              </div>
            </div>
          </Dialog>
        </Root>
      )}
    </Root>
  )
}
export default AgreementAdjustment
